'use client';

import { IconChevronLeftFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-left-filled';
import { interactionResponse } from '@zillow/yield-callback';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { cx } from '@/styled-system/css';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { PhotoCarouselRootContext } from './context/photo-carousel-root.context';
import { photoCarouselNavButtonRecipe } from './styles/photo-carousel-nav-button.styles';
import { photoCarouselPreviousButtonStyles } from './styles/photo-carousel-previous-button.styles';
import type { PhotoCarouselPreviousButtonPropsInterface } from './types/photo-carousel-previous-button.types';

/**
 * Control used to show the previous photo in the carousel.
 *
 * Renders as a span instead of a button and uses `aria-hidden` so that it's
 * hidden from the tab order (keyboard controls are provided by `PhotoCarousel.Dots` instead).
 *
 * @related ['IconButton']
 * @since 10.0.0
 */

export const PhotoCarouselPreviousButton = forwardRef<
  HTMLButtonElement,
  PhotoCarouselPreviousButtonPropsInterface
>((props: PhotoCarouselPreviousButtonPropsInterface, forwardedRef) => {
  const {
    activeIndex = 0,
    shouldAwaitInteractionResponse,
    shouldLoop = true,
    updateActiveIndex,
  } = useContext(PhotoCarouselRootContext);
  const {
    children = (
      <Icon>
        <IconChevronLeftFilled />
      </Icon>
    ),
    css: cssProp,
    disabled = !shouldLoop && activeIndex === 0,
    onClick,
    ...rest
  } = props;

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (event) => {
      // Stop propagation immediately to prevent invoking Property Card's `onClick`
      event.stopPropagation();

      if (disabled) return;

      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateActiveIndex?.(activeIndex - 1);
      onClick?.(event);
    },
    [activeIndex, disabled, onClick, shouldAwaitInteractionResponse, updateActiveIndex],
  );

  return (
    <IconButton
      data-c11n-component="PhotoCarousel.PreviousButton"
      disabled={disabled}
      emphasis="bare"
      onClick={handleClick}
      onImpact
      ref={forwardedRef}
      tabIndex={-1}
      title="Previous photo"
      tone="neutral"
      css={{
        ...photoCarouselNavButtonRecipe.raw({ disabled }),
        ...photoCarouselPreviousButtonStyles,
        ...cssProp,
      }}
      {...rest}
      className={cx('c11n_photo-carousel__previous-button', rest.className)}
    >
      {children}
    </IconButton>
  );
});

PhotoCarouselPreviousButton.displayName = 'PhotoCarousel.PreviousButton';
